/**
 * pogi-qol.js — quality-of-life UX polish. PURELY ADDITIVE & non-blocking: nothing
 * here preventDefaults a navigation, a form submit, or an add to cart. Delete the
 * <script> tag in layout/theme.liquid to remove all of it cleanly.
 *
 * ES5-only (var / function exprs / index for-loops — no arrow/const/let/template
 * literals/optional chaining/NodeList.forEach). Feature-detected, idempotent,
 * reduced-motion + Save-Data aware. Hardened after a 4-lens adversarial audit
 * (functional / races / cross-browser-mobile / a11y) — see the worklog.
 *
 * Features:
 *   1. window.pogiAnnounce(msg) — a polite SR live region for transient button
 *      states. MODAL-AWARE: when a <dialog> is open (the cart drawer) it writes into
 *      a region INSIDE that dialog, because showModal() makes the rest of the page
 *      inert and a body-level live region would be swallowed.
 *   2. Add-intent correlation — remember a valid Pogi add click without opening
 *      the drawer; the standard cart event owns render-before-open ordering.
 *   3. Haptic — a faint tick on that confirmed add (Android; iOS ignores vibrate). The
 *      header cart bubble already animates natively in Horizon, so we add no 2nd bump.
 *   4. Intent prefetch — prefetch a product/collection/page link on hover/touch.
 *   5. Image fade-in — lazy images that haven't loaded yet fade in instead of popping.
 */
(function () {
  'use strict';

  var doc = document;
  var REDUCE = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  // ===========================================================================
  // 1) Screen-reader announcer (modal-aware)
  // ===========================================================================
  function makeRegion() {
    var el = doc.createElement('div');
    el.className = 'visually-hidden';
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-atomic', 'true');
    return el;
  }
  function bodyRegion() {
    var r = doc.getElementById('pogi-sr');
    if (!r) {
      r = makeRegion();
      r.id = 'pogi-sr';
      (doc.body || doc.documentElement).appendChild(r);
    }
    return r;
  }
  function dialogRegion(dlg) {
    var r = dlg.querySelector('[data-pogi-sr]');
    if (!r) {
      r = makeRegion();
      r.setAttribute('data-pogi-sr', '1');
      dlg.appendChild(r);
    }
    return r;
  }
  var srTimer, srSetTimer;
  // Exposed so pogi-atc-feedback.js can announce 'Adding to cart'/'Checking out'/etc.
  window.pogiAnnounce = function (msg) {
    if (!msg) return;
    clearTimeout(srTimer);
    // Defer briefly so a standard-event-owned drawer open can complete and we
    // can target the non-inert live region. ~80ms is imperceptible for AT.
    srTimer = window.setTimeout(function () {
      try {
        // Target a live region inside the TOPMOST open modal (showModal makes the rest
        // of the page inert, so a body region would be swallowed). Reuse the drawer's
        // pre-existing ref="liveRegion" when present — more reliable than a region we
        // insert and populate dynamically — else an in-dialog region, else the body one.
        var modals = doc.querySelectorAll('dialog[open]');
        var modal = modals.length ? modals[modals.length - 1] : null;
        var target = modal ? (modal.querySelector('[ref="liveRegion"]') || dialogRegion(modal)) : bodyRegion();
        target.textContent = ''; // clear first so an identical repeat re-announces
        clearTimeout(srSetTimer);
        srSetTimer = window.setTimeout(function () { target.textContent = String(msg); }, 30);
      } catch (e) { /* never let an announcement break anything */ }
    }, 80);
  };

  // ===========================================================================
  // 2) Add-intent correlation  +  3) haptic
  //    Horizon's standard cart event is the only drawer owner. Opening here on
  //    click would expose stale/empty content before /cart/add.js and the returned
  //    drawer section settle. We retain only a short-lived intent bit so a
  //    confirmed matching add can provide haptic feedback.
  // ===========================================================================
  var pogiAddOwner = null;
  var intentTimer;
  var INTENT_TIMEOUT_MS = 2500;

  function haptic() {
    if (navigator.vibrate) { try { navigator.vibrate(8); } catch (e) {} }
  }

  function ownerForButton(btn) {
    var formId = btn.getAttribute('form');
    var form = formId ? doc.getElementById(formId) : btn.form;
    return form && typeof form.closest === 'function'
      ? form.closest('product-form-component')
      : null;
  }

  function ownerForNode(node) {
    if (!node || node.nodeType !== 1) return null;
    if (node.matches && node.matches('product-form-component')) return node;
    return typeof node.closest === 'function'
      ? node.closest('product-form-component')
      : null;
  }

  doc.addEventListener('click', function (e) {
    var node = e.target;
    if (node && node.nodeType === 3) node = node.parentNode;
    if (!node || typeof node.closest !== 'function') return;
    var btn = node.closest('[data-pogi-atc]');
    if (!btn) return;
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    if (/sold\s*out/i.test(btn.textContent || '')) return;
    var owner = ownerForButton(btn);
    if (!owner) return;
    pogiAddOwner = owner;
    clearTimeout(intentTimer);
    intentTimer = window.setTimeout(function () {
      pogiAddOwner = null;
    }, INTENT_TIMEOUT_MS);
  });

  function clearAddIntent() {
    clearTimeout(intentTimer);
    pogiAddOwner = null;
  }

  doc.addEventListener('shopify:cart:lines-update', function (e) {
    if (!e || e.action !== 'add' || !e.promise) return;
    var eventOwner = ownerForNode(e.target);
    if (!eventOwner || eventOwner !== pogiAddOwner) return;
    e.promise.then(function (result) {
      var failed = !!(result && result.detail && result.detail.didError);
      var wasOurs = eventOwner === pogiAddOwner;
      clearAddIntent();
      if (!failed && wasOurs) haptic(); // tick only on the correlated Pogi add
    }).catch(function () {
      clearAddIntent();
    });
  });

  // ===========================================================================
  // 4) Intent prefetch
  // ===========================================================================
  (function () {
    var conn = navigator.connection;
    if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return; // data saver
    var done = {};
    var count = 0;
    var MAX = 12;
    var hoverTimer, touchTimer;

    function eligible(a) {
      if (!a || !a.href || a.target === '_blank' || a.hasAttribute('download')) return null;
      if (/external|nofollow/i.test(a.getAttribute('rel') || '')) return null;
      var href = a.href;
      if (href.indexOf(location.origin) !== 0) return null;                 // same-origin only
      if (href.split('#')[0] === location.href.split('#')[0]) return null;  // skip current page / #anchors
      // navigational content pages only, allowing an optional /xx or /xx-yy locale prefix
      if (!/^(\/[a-z]{2}(-[a-z]{2})?)?\/(products|collections|pages|blogs)\//i.test(a.pathname || '')) return null;
      if (done[href]) return null;
      return href;
    }
    function prefetch(href) {
      if (!href || done[href] || count >= MAX) return;
      done[href] = true;
      count++;
      var link = doc.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      link.as = 'document';
      (doc.head || doc.documentElement).appendChild(link);
    }
    function anchorFrom(e) {
      var node = e.target;
      if (node && node.nodeType === 3) node = node.parentNode;
      if (!node || typeof node.closest !== 'function') return null;
      return node.closest('a[href]');
    }
    // Mouse: hover-intent debounce.
    doc.addEventListener('mouseover', function (e) {
      var href = eligible(anchorFrom(e));
      if (!href) return;
      clearTimeout(hoverTimer);
      hoverTimer = window.setTimeout(function () { prefetch(href); }, 70);
    }, true);
    doc.addEventListener('mouseout', function () { clearTimeout(hoverTimer); }, true);
    // Keyboard focus: strong intent, go now.
    doc.addEventListener('focusin', function (e) {
      var href = eligible(anchorFrom(e));
      if (href) prefetch(href);
    }, true);
    // Touch: schedule, but cancel if the finger moves (a scroll, not a tap) — no
    // prefetching full pages from tap-and-scroll on mobile data.
    doc.addEventListener('touchstart', function (e) {
      var href = eligible(anchorFrom(e));
      if (!href) return;
      clearTimeout(touchTimer);
      touchTimer = window.setTimeout(function () { prefetch(href); }, 120);
    }, { capture: true, passive: true });
    doc.addEventListener('touchmove', function () { clearTimeout(touchTimer); }, { capture: true, passive: true });
    doc.addEventListener('scroll', function () { clearTimeout(touchTimer); }, { capture: true, passive: true });
  })();

  // ===========================================================================
  // 5) Image fade-in (lazy images that aren't loaded yet) — verified a11y/hidden-safe
  // ===========================================================================
  (function () {
    if (REDUCE) return;
    function arm(img) {
      if (!img || img.getAttribute('data-pogi-fade') === '1') return;
      if (img.complete) return;           // already painted — never hide it
      if (img.loading !== 'lazy') return; // only below-the-fold images
      img.setAttribute('data-pogi-fade', '1');
      img.classList.add('pogi-fade');
    }
    function show(img) {
      if (img && img.classList && img.classList.contains('pogi-fade')) img.classList.add('pogi-in');
    }
    function pass() {
      var imgs = doc.querySelectorAll('img[loading="lazy"]');
      for (var i = 0; i < imgs.length; i++) arm(imgs[i]);
    }
    // 'load' doesn't bubble; capture-phase on document still catches every image load.
    doc.addEventListener('load', function (e) {
      if (e.target && e.target.tagName === 'IMG') show(e.target);
    }, true);
    if (doc.readyState !== 'loading') pass();
    else doc.addEventListener('DOMContentLoaded', pass);
    // Watchdog: never leave a faded image invisible if its load/error event was missed.
    window.setTimeout(function () {
      var faded = doc.querySelectorAll('img.pogi-fade');
      for (var i = 0; i < faded.length; i++) show(faded[i]);
    }, 3000);
  })();
})();
